import type { CheckoutRuntimeConfig } from '../theme/types';

declare global {
  interface Window {
    __LOCTOPUS_CHECKOUT_CONFIG__?: CheckoutRuntimeConfig;
  }
}

const DEFAULT_CONFIG_PATH = '/loctopus-config.json';

export class ConfigLoadError extends Error {}

// Set once config loads successfully, so lib/errorDisplay.ts can show which API address a later
// network failure tried to reach, without every page needing to thread the config down to it.
let lastKnownBaseUrl: string | undefined;

export function getLastKnownBaseUrl(): string | undefined {
  return lastKnownBaseUrl;
}

function assertValid(value: unknown, source: string): CheckoutRuntimeConfig {
  if (typeof value !== 'object' || value === null) {
    throw new ConfigLoadError(`Checkout config from ${source} is not a JSON object.`);
  }
  const cfg = value as Record<string, unknown>;
  const missing: string[] = [];
  if (typeof cfg.baseUrl !== 'string' || cfg.baseUrl === '') missing.push('baseUrl');
  if (typeof cfg.publicKey !== 'string' || cfg.publicKey === '') missing.push('publicKey');
  if (typeof cfg.turnstileSiteKey !== 'string' || cfg.turnstileSiteKey === '') {
    missing.push('turnstileSiteKey');
  }
  if (typeof cfg.theme !== 'object' || cfg.theme === null) missing.push('theme');
  if (missing.length > 0) {
    throw new ConfigLoadError(
      `Checkout config from ${source} is missing required field(s): ${missing.join(', ')}. ` +
        'Expected { baseUrl, publicKey, turnstileSiteKey, theme } - see clients/_template/theme.json for the theme shape.',
    );
  }
  lastKnownBaseUrl = cfg.baseUrl as string;
  return cfg as unknown as CheckoutRuntimeConfig;
}

/**
 * Resolution order (first match wins), so the same build works for local dev, a static host that
 * cannot serve a second JSON file, and the normal per-client deployment:
 *  1. `window.__LOCTOPUS_CHECKOUT_CONFIG__` - inlined by a client's own index.html.
 *  2. `?config=<url>` query param - lets one preview deployment point at any client's config.
 *  3. `VITE_CONFIG_URL` build-time default, else `/loctopus-config.json` same-origin.
 */
export async function loadRuntimeConfig(): Promise<CheckoutRuntimeConfig> {
  if (window.__LOCTOPUS_CHECKOUT_CONFIG__) {
    return assertValid(window.__LOCTOPUS_CHECKOUT_CONFIG__, 'window.__LOCTOPUS_CHECKOUT_CONFIG__');
  }

  const params = new URLSearchParams(window.location.search);
  const url = params.get('config') ?? import.meta.env.VITE_CONFIG_URL ?? DEFAULT_CONFIG_PATH;

  let response: Response;
  try {
    response = await fetch(url, { headers: { Accept: 'application/json' } });
  } catch (cause) {
    throw new ConfigLoadError(
      `Could not fetch checkout config from "${url}": ${cause instanceof Error ? cause.message : String(cause)}. ` +
        'Deploy a loctopus-config.json next to this build, or inline window.__LOCTOPUS_CHECKOUT_CONFIG__.',
    );
  }
  if (!response.ok) {
    throw new ConfigLoadError(
      `Checkout config fetch to "${url}" returned HTTP ${response.status}.`,
    );
  }
  const json = await response.json();
  return assertValid(json, url);
}
